import Box, { BoxProps } from '@material-ui/core/Box';
import { useWindowSize } from 'hooks/useWindowSize';
import React from 'react';

type TextStyleKeyType =
  | 'displayBold'
  | 'titleBold'
  | 'h1Bold'
  | 'h1Medium'
  | 'h2Bold'
  | 'h2Medium'
  | 'h3Bold'
  | 'h3Medium'
  | 'h3Regular'
  | 'subHeadingBold'
  | 'subHeadingMedium'
  | 'subHeadingRegular'
  | 'p1Medium'
  | 'p1Regular'
  | 'p2Bold'
  | 'p2Medium'
  | 'p2Regular'
  | 'p3Medium'
  | 'p3Regular'
  | 'p3Bold'
  | 'caption1'
  | 'caption2'
  | 'caption3';

const breakPointAndIndexMapper = {
  xs: 0,
  sm: 1,
  md: 2,
  lg: 4,
  xl: 5
};

const getStyle = (
  width: number,
  styles: TextStyleKeyType | Array<TextStyleKeyType>
) => {
  const isList = Array.isArray(styles);
  const styleList = isList ? styles : [styles];

  if (!isList || !styles) {
    return styles;
  }

  const xsStyle = styleList[breakPointAndIndexMapper.xs];
  const smStyle = styleList[breakPointAndIndexMapper.sm] ?? xsStyle;
  const mdStyle = styleList[breakPointAndIndexMapper.md] ?? smStyle;
  const lgStyle = styleList[breakPointAndIndexMapper.lg] ?? mdStyle;
  const xlStyle = styleList[breakPointAndIndexMapper.xl] ?? lgStyle;

  if (width < 600) {
    return xsStyle;
  }

  if (width >= 600 && width < 768) {
    return smStyle;
  }

  if (width >= 768 && width < 1280) {
    return mdStyle;
  }

  if (width >= 1280 && width < 1920) {
    return lgStyle;
  }

  return xlStyle;
};

interface TextProps extends BoxProps {
  textStyle: TextStyleKeyType | TextStyleKeyType[];
}

/**
 *  User can use this component to apply style on text based on design system. Example: h1Medium, h2Medium, etc.
 *  textStyle: className or array of classnames(If there is style based on screen size). Please refers "TextStyleKeyType" for className.
 *  Note: You can also apply box properties(fontSize, style, etc.)
 *  Usage: <BaseText textStyle="p1Medium" color="var(--neutral-600)">content</BaseText>
 */
const BaseText: React.FC<TextProps> = ({ textStyle, className, ...props }) => {
  const { width } = useWindowSize();
  const textStyleKey = getStyle(width ?? window?.innerWidth, textStyle);
  // eslint-disable-next-line react/destructuring-assignment

  return (
    <Box
      component="p"
      {...props}
      className={`${textStyleKey ?? ''} ${className ?? ''}`}
    />
  );
};

export default BaseText;
